import React, { useState, useRef, useCallback } from "react";
import { Upload, Trash2, ArrowUp, ArrowDown, Star, Loader2, AlertCircle } from "lucide-react";
import { compressImageFile, isImageFile, formatBytes } from "@/lib/imageUtils";

const HARD_MAX_IMAGES = 8;
const HARD_MAX_BYTES = 3 * 1024 * 1024;

/**
 * AdminImageUploader
 *
 * Multi-image picker that compresses each file client-side and emits an
 * array of project-image-shaped objects ready to ship to the backend:
 *   { id?, filename, content_type, size, data_url, alt, is_before, sort_order }
 *
 * The parent owns the images array and passes it back via `onChange`.
 */
export default function AdminImageUploader({ images = [], onChange, isBeforeAfter = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);

  const remainingSlots = Math.max(0, HARD_MAX_IMAGES - images.length);

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter(isImageFile);
      if (files.length === 0) return;
      const issues = [];
      const accepted = files.slice(0, remainingSlots);
      if (files.length > remainingSlots) {
        issues.push(`Only ${remainingSlots} more image${remainingSlots === 1 ? "" : "s"} can be added (max ${HARD_MAX_IMAGES}).`);
      }

      setBusy(true);
      const next = [...images];
      for (const file of accepted) {
        try {
          const out = await compressImageFile(file);
          if (out.size > HARD_MAX_BYTES) {
            issues.push(`${file.name}: still ${formatBytes(out.size)} after compression. Try a smaller image.`);
            continue;
          }
          next.push({
            filename: out.filename,
            content_type: out.content_type,
            size: out.size,
            data_url: out.data_url,
            alt: "",
            is_before: false,
            sort_order: next.length,
          });
        } catch (err) {
          issues.push(`${file.name}: failed to read image.`);
        }
      }
      setBusy(false);
      setErrors(issues);
      onChange(next.map((img, i) => ({ ...img, sort_order: i })));
    },
    [images, remainingSlots, onChange]
  );

  const handleInput = (e) => handleFiles(e.target.files);
  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer?.files) handleFiles(e.dataTransfer.files);
  };
  const stopEvent = (e) => e.preventDefault();

  const removeAt = (idx) => {
    const next = images.filter((_, i) => i !== idx).map((img, i) => ({ ...img, sort_order: i }));
    onChange(next);
  };

  const moveAt = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= images.length) return;
    const next = [...images];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange(next.map((img, i) => ({ ...img, sort_order: i })));
  };

  const updateAt = (idx, patch) => {
    const next = images.map((img, i) => (i === idx ? { ...img, ...patch } : img));
    onChange(next);
  };

  return (
    <div className="space-y-4" data-testid="admin-image-uploader">
      <div
        onDrop={handleDrop}
        onDragOver={stopEvent}
        onDragEnter={stopEvent}
        className="border-2 border-dashed border-gray-300 rounded-lg bg-br-paper/40 p-6 text-center hover:border-br-bronze transition"
      >
        <Upload size={26} className="mx-auto text-br-bronze" />
        <div className="mt-2 font-display tracking-wider uppercase text-sm text-br-black">Drop images here</div>
        <div className="text-xs text-gray-500 mt-1">JPG, PNG, or HEIC. Each image is auto-resized and compressed.</div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy || remainingSlots === 0}
          className="br-btn-dark mt-3 text-[11px]"
          data-testid="image-uploader-pick"
        >
          {busy ? <><Loader2 size={12} className="animate-spin" /> Processing…</> : `Browse — ${remainingSlots} slot${remainingSlots === 1 ? "" : "s"} left`}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleInput}
          data-testid="image-uploader-input"
        />
      </div>

      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700 space-y-1" data-testid="image-uploader-errors">
          {errors.map((e, i) => (
            <div key={i} className="flex items-start gap-1.5"><AlertCircle size={12} className="mt-0.5 shrink-0" /> {e}</div>
          ))}
        </div>
      )}

      {images.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="image-uploader-list">
          {images.map((img, idx) => (
            <div key={img.id || idx} className="relative bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`image-uploader-tile-${idx}`}>
              <div className="relative aspect-[4/3] bg-br-paper">
                <img src={img.data_url} alt={img.alt || ""} className="w-full h-full object-cover" />
                {idx === 0 && (
                  <div className="absolute top-2 left-2 bg-br-bronze text-white text-[9px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded flex items-center gap-1">
                    <Star size={9} style={{ fill: "#fff" }} /> Cover
                  </div>
                )}
              </div>
              <div className="p-2.5 space-y-2">
                <input
                  type="text"
                  className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                  placeholder="Optional caption / alt text"
                  value={img.alt || ""}
                  onChange={(e) => updateAt(idx, { alt: e.target.value })}
                  data-testid={`image-alt-${idx}`}
                />
                <div className="flex items-center justify-between gap-2 text-[11px] text-gray-500">
                  <span>{formatBytes(img.size || 0)}</span>
                  {isBeforeAfter && (
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        className="accent-br-bronze"
                        checked={!!img.is_before}
                        onChange={(e) => updateAt(idx, { is_before: e.target.checked })}
                        data-testid={`image-before-${idx}`}
                      />
                      Before photo
                    </label>
                  )}
                </div>
                <div className="flex items-center justify-between gap-1">
                  <div className="flex gap-1">
                    <button type="button" onClick={() => moveAt(idx, -1)} disabled={idx === 0} className="p-1 text-gray-500 hover:text-br-bronze disabled:opacity-30" aria-label="Move up">
                      <ArrowUp size={14} />
                    </button>
                    <button type="button" onClick={() => moveAt(idx, 1)} disabled={idx === images.length - 1} className="p-1 text-gray-500 hover:text-br-bronze disabled:opacity-30" aria-label="Move down">
                      <ArrowDown size={14} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    className="p-1 text-red-500 hover:text-red-700"
                    aria-label="Remove image"
                    data-testid={`image-remove-${idx}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
